import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Alert, Spinner, Form, Row, Col } from 'react-bootstrap';
import { FaDownload, FaSave, FaEye, FaEdit, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import drawingGeneratorService from '../services/drawingGeneratorService';
import doorService from '../services/doorService';

const DrawingGeneratorModal = ({ 
  show, 
  onHide, 
  doorMetadata, 
  projectInfo, 
  folderId,
  onPdfSaved,
  userProfile 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatorUrl, setGeneratorUrl] = useState('');
  const [showDimensionEditor, setShowDimensionEditor] = useState(false);
  const [editedDimensions, setEditedDimensions] = useState({
    width: doorMetadata?.width || 988,
    height: doorMetadata?.height || 2078
  });
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef(null);
  const [pdfReceived, setPdfReceived] = useState(false);

  useEffect(() => {
    if (show && doorMetadata) {
      initializeDrawingGenerator();
    }
  }, [show, doorMetadata]);

  // Listen for PDF messages from iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        console.warn('⚠️ Ignoring message from unknown origin:', event.origin);
        return;
      }

      // Check if this is a PDF message from drawing generator
      if (event.data && event.data.type === 'PDF_GENERATED') {
        console.log('📥 Received PDF from drawing generator');
        await handlePdfFromIframe(event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [folderId, doorMetadata, projectInfo]);

  const initializeDrawingGenerator = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🎨 Initializing drawing generator...');
      
      // Check if drawing generator is accessible
      console.log('🔍 Checking drawing generator accessibility...');
      const isAccessible = await drawingGeneratorService.validateDrawingGenerator();
      console.log('✅ Drawing generator accessible:', isAccessible);
      
      if (!isAccessible) {
        // Provide more detailed error information
        const isDevelopment = process.env.NODE_ENV !== 'production';
        const testUrl = isDevelopment 
          ? 'http://localhost:5000/drawinggenerator/index.html'
          : `${window.location.origin}/drawinggenerator/index.html`;
        
        const errorMsg = `Drawing generator is not accessible. 
        
Possible solutions:
1. Ensure BOTH servers are running:
   - React dev server (port 3000): npm run client
   - Express server (port 5000): npm run server
   - Or use: npm run dev (runs both)

2. Check that the drawinggenerator folder exists in the project root

3. Verify the Express server is serving static files from /drawinggenerator route

4. Try accessing the drawing generator directly in your browser:
   ${testUrl}

5. Check browser console for CORS or network errors

Current URL being tested: ${testUrl}`;
        
        throw new Error(errorMsg);
      }

      // Prepare drawing generator with door data
      console.log('🚪 Preparing drawing generator with door data:', doorMetadata);
      const result = await drawingGeneratorService.openDrawingGenerator(doorMetadata, projectInfo, userProfile);
      
      if (result.success) {
        console.log('✅ Drawing generator URL generated:', result.url);
        setGeneratorUrl(result.url);
        setEditedDimensions({
          width: result.data.width,
          height: result.data.height
        });
      } else {
        throw new Error(result.error || 'Failed to initialize drawing generator');
      }
    } catch (err) {
      console.error('❌ Error initializing drawing generator:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDimensionUpdate = async () => {
    try {
      // Update the iframe URL with new dimensions
      const updatedDoorData = {
        ...doorMetadata,
        width: editedDimensions.width,
        height: editedDimensions.height
      };
      
      const result = await drawingGeneratorService.openDrawingGenerator(updatedDoorData, projectInfo, userProfile);
      if (result.success) {
        setGeneratorUrl(result.url);
        setShowDimensionEditor(false);
        toast.success('Dimensions updated successfully');
        
        // Reload the iframe
        if (iframeRef.current) {
          iframeRef.current.src = result.url;
        }
      }
    } catch (err) {
      toast.error('Failed to update dimensions: ' + err.message);
    }
  };

  // Handle PDF received from iframe
  const handlePdfFromIframe = async (data) => {
    setSaving(true);
    try {
      console.log('📦 Processing PDF from drawing generator:', data);
      
      // Convert base64 to blob
      const pdfBlob = base64ToBlob(data.pdfData, 'application/pdf');
      const filename = data.filename || `CO-${projectInfo?.name || 'drawing'}.pdf`;
      
      // Create File object from blob
      const pdfFile = new File([pdfBlob], filename, { 
        type: 'application/pdf',
        lastModified: Date.now()
      });
      
      console.log('💾 Saving PDF file:', filename, 'Size:', pdfFile.size, 'bytes');
      
      // Save to folder
      const saveResult = await drawingGeneratorService.savePdfToFolder(
        folderId, 
        pdfFile, 
        filename, 
        doorMetadata,
        projectInfo?.id
      );
      
      if (saveResult.success) {
        setPdfReceived(true);
        toast.success('✅ Drawing PDF saved successfully!');
        if (onPdfSaved) {
          onPdfSaved(saveResult);
        }
        // Auto-close modal after 1.5 seconds
        setTimeout(() => {
          onHide();
          setPdfReceived(false);
        }, 1500);
      } else {
        throw new Error(saveResult.error);
      }
      
    } catch (err) {
      console.error('❌ Error saving PDF:', err);
      toast.error('Failed to save PDF: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Helper function to convert base64 to blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const handleSavePdf = async () => {
    // Trigger PDF generation in iframe
    if (iframeRef.current && iframeRef.current.contentWindow) {
      console.log('📤 Requesting PDF generation from drawing generator...');
      iframeRef.current.contentWindow.postMessage(
        { type: 'GENERATE_PDF_FOR_PARENT' },
        window.location.origin
      );
      toast.info('Generating PDF... Please wait.');
    } else {
      toast.error('Drawing generator not ready. Please try again.');
    }
  };

  const handleUpdateDoorMetadata = async () => {
    try {
      // Update door metadata with edited dimensions
      const updatedMetadata = {
        ...doorMetadata,
        width: editedDimensions.width,
        height: editedDimensions.height
      };
      
      await doorService.saveDoorMetadata(folderId, updatedMetadata);
      toast.success('Door metadata updated successfully');
    } catch (err) {
      toast.error('Failed to update door metadata: ' + err.message);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="drawing-generator-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEdit className="me-2" />
          Auto-Generate Drawings
          {doorMetadata && (
            <small className="text-muted ms-2">
              {doorMetadata.doorName} - {editedDimensions.width}x{editedDimensions.height}mm
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ height: '80vh', padding: 0 }}>
        {loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading drawing generator...</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="p-3">
            <Alert variant="danger">
              <strong>Error:</strong> {error}
            </Alert>
          </div>
        )}
        
        {!loading && !error && generatorUrl && (
          <>
            {/* Dimension Editor */}
            {showDimensionEditor && (
              <div className="p-3 border-bottom bg-light">
                <h6>Adjust Dimensions</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Width (mm)</Form.Label>
                      <Form.Control
                        type="number"
                        value={editedDimensions.width}
                        onChange={(e) => setEditedDimensions(prev => ({
                          ...prev,
                          width: parseFloat(e.target.value) || 0
                        }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Height (mm)</Form.Label>
                      <Form.Control
                        type="number"
                        value={editedDimensions.height}
                        onChange={(e) => setEditedDimensions(prev => ({
                          ...prev,
                          height: parseFloat(e.target.value) || 0
                        }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Button 
                      variant="primary" 
                      onClick={handleDimensionUpdate}
                      className="me-2"
                    >
                      Update Drawing
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={() => setShowDimensionEditor(false)}
                    >
                      <FaTimes />
                    </Button>
                  </Col>
                </Row>
              </div>
            )}
            
            {/* Drawing Generator Iframe */}
            <iframe
              ref={iframeRef}
              src={generatorUrl}
              style={{ 
                width: '100%', 
                height: showDimensionEditor ? 'calc(100% - 120px)' : '100%',
                border: 'none'
              }}
              title="Drawing Generator"
            />
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            <Button 
              variant="outline-primary" 
              onClick={() => setShowDimensionEditor(!showDimensionEditor)}
              disabled={loading || error}
            >
              <FaEdit className="me-1" />
              {showDimensionEditor ? 'Hide' : 'Edit'} Dimensions
            </Button>
          </div>
          
          <div>
            <Button 
              variant="secondary" 
              onClick={onHide}
              className="me-2"
              disabled={saving}
            >
              Close
            </Button>
            
            <Button 
              variant="success" 
              onClick={handleSavePdf}
              disabled={loading || error || saving || pdfReceived}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving PDF...
                </>
              ) : pdfReceived ? (
                <>
                  ✅ PDF Saved!
                </>
              ) : (
                <>
                  <FaSave className="me-1" />
                  Save PDF to Drawings
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DrawingGeneratorModal;
